import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CalendarCheck, X, Sparkles, ArrowRight, ShieldCheck, Check } from "lucide-react";
import { useDemo } from "@/components/landing/DemoContext";
import { Counter } from "@/components/landing/Counter";
import { MobileHero } from "@/components/landing/MobileHero";

const REPLACED_TOOLS = [
  { name: "Hootsuite",     category: "Social",      domain: "hootsuite.com",   price: 99,  replaceWith: "Marketing module"  },
  { name: "SharpSpring",   category: "Marketing",   domain: "sharpspring.com", price: 449, replaceWith: "Marketing + CRM"   },
  { name: "ManyChat",      category: "Chatbot",     domain: "manychat.com",    price: 15,  replaceWith: "AI Receptionist"   },
  { name: "Kajabi",        category: "Courses",     domain: "kajabi.com",      price: 149, replaceWith: "Manuals module"    },
  { name: "Call Hero",     category: "Calls",       domain: "callhero.com",    price: 225, replaceWith: "Call IQ AI"        },
  { name: "GradeUs",       category: "Reviews",     domain: "gradeus.com",     price: 110, replaceWith: "Reactivation flow" },
  { name: "Zapier",        category: "Automation",  domain: "zapier.com",      price: 69,  replaceWith: "Automation builder"},
  { name: "Skool",         category: "Community",   domain: "skool.com",       price: 99,  replaceWith: "Manuals · KB"      },
  { name: "HubSpot",       category: "CRM",         domain: "hubspot.com",     price: 450, replaceWith: "Patient profile"   },
  { name: "Website Forms", category: "Forms",       domain: "typeform.com",    price: 25,  replaceWith: "Form Builder"      },
  { name: "Intercom",      category: "Live Chat",   domain: "intercom.com",    price: 74,  replaceWith: "Unified Inbox"     },
  { name: "Retell AI",     category: "AI Voice",    domain: "retellai.com",    price: 200, replaceWith: "AI Receptionist"   },
];

const TOTAL_REPLACED = REPLACED_TOOLS.reduce((a, t) => a + t.price, 0);

/* Polished tool card with hover reveal of what Clinic OS replaces it with */
const ToolCard = ({ t, i }) => (
  <motion.div
    variants={{
      hidden: { opacity: 0, y: 12 },
      show:   { opacity: 1, y: 0 },
    }}
    className="group relative rounded-2xl border border-border bg-card p-3.5 shadow-sm transition-all hover:-translate-y-1 hover:border-violet-400/45 hover:shadow-lg hover:shadow-violet-500/10"
    data-testid={`replaced-tool-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <motion.span
      initial={{ scale: 0, rotate: -45 }}
      animate={{ scale: 1, rotate: -10 }}
      transition={{ duration: 0.35, delay: 0.9 + i * 0.04, type: "spring", stiffness: 220 }}
      className="absolute -right-2 -top-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-rose-600 text-white shadow-md ring-2 ring-card group-hover:from-emerald-500 group-hover:to-emerald-600"
      aria-hidden="true"
    >
      <X className="h-3 w-3 group-hover:hidden" strokeWidth={3.5} />
      <Check className="hidden h-3 w-3 group-hover:block" strokeWidth={3.5} />
    </motion.span>

    <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-secondary/50 transition-colors group-hover:border-violet-300/40">
      <img decoding="async"
        src={`https://www.google.com/s2/favicons?domain=${t.domain}&sz=256`}
        alt={`${t.name} logo`}
        loading="lazy"
        className="h-6 w-6 rounded-md object-contain transition-all group-hover:scale-105 group-hover:grayscale"
      />
    </div>

    <p className="mt-2.5 truncate font-display text-[14px] font-black leading-tight tracking-[-0.01em] text-foreground transition-colors">
      {t.name}
    </p>
    <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/85">
      {t.category}
    </p>
    <p className="mt-1.5 text-xs font-bold text-rose-500 line-through decoration-rose-500/70 decoration-[1.5px]">
      ${t.price}/mo
    </p>

    {/* Hover reveal — Clinic OS replacement */}
    <div className="pointer-events-none absolute inset-x-0 -bottom-2 mx-2 translate-y-2 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
      <div className="rounded-lg bg-gradient-to-r from-violet-600 to-blue-600 px-2 py-1 text-[9.5px] font-bold uppercase tracking-[0.12em] text-white shadow-lg shadow-violet-600/40">
        <Sparkles className="-mt-0.5 mr-0.5 inline h-2.5 w-2.5" />
        → {t.replaceWith}
      </div>
    </div>
  </motion.div>
);

const REASSURANCES = [
  "Migration assistance",
  "No credit card",
  "HIPAA & SOC 2 compliant",
];

/* ============================================================================
   PROBLEM-FOCUSED HERO — polished, modernized
   =========================================================================== */
export const ClinicOSHero = () => {
  const openDemo = useDemo();

  return (
    <>
      {/* Dedicated mobile hero — purpose-built, NOT a shrunk desktop */}
      <MobileHero totalReplaced={TOTAL_REPLACED} />

      {/* Desktop hero — hidden on mobile */}
      <section
        id="top"
        data-testid="hero-section"
        className="section-hero relative hidden overflow-hidden pt-20 pb-12 sm:block sm:pt-24 sm:pb-16 lg:pt-24"
      >
      <div className="relative z-10 mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
        {/* Announcement strip — centered, sits at the very top of the hero */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mx-auto mb-8 flex justify-center sm:mb-10"
        >
          <Link
            to="/kai"
            data-testid="hero-announcement"
            className="group inline-flex items-center gap-2.5 rounded-full border border-border bg-card/80 px-3 py-1.5 text-xs backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-foreground/30 hover:shadow-sm"
          >
            <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-cyan-500 to-emerald-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white">
              <Sparkles className="h-2.5 w-2.5" /> New
            </span>
            <span className="font-medium text-foreground/80">
              KAI is live inside Leads &amp; Inbox
            </span>
            <ArrowRight className="h-3 w-3 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
          </Link>
        </motion.div>

        <div className="grid gap-10 lg:grid-cols-12 lg:gap-14">
          {/* ─────────  LEFT  ───────── */}
          <div className="lg:col-span-6" data-testid="hero-problem-side">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.05 }}
              className="inline-flex items-center gap-2 rounded-full border border-rose-500/40 bg-rose-500/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.22em] text-rose-600 dark:text-rose-400"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-rose-500" />
              </span>
              The problem
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-5 font-display text-[2.25rem] font-black leading-[1.02] tracking-[-0.035em] sm:text-5xl lg:text-[3.75rem]"
            >
              You didn&apos;t open a clinic to{" "}
              <span className="bg-gradient-to-r from-rose-500 to-orange-500 bg-clip-text text-transparent">
                manage 12 logins.
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.18 }}
              className="mt-5 max-w-xl text-[15px] leading-relaxed text-muted-foreground sm:text-base"
            >
              Most clinics stitch together a dozen tools to keep the front desk
              afloat. Every subscription costs money. Every disconnect costs patients.
              And the team spends more time{" "}
              <span className="font-semibold text-foreground">switching tabs</span>{" "}
              than caring for people.
            </motion.p>

            {/* Refined cost callout — data-viz feel with highlighted pain stats */}
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.26 }}
              className="mt-6 inline-flex flex-col gap-3.5 rounded-2xl border border-rose-500/30 bg-gradient-to-br from-rose-500/12 via-rose-500/8 to-orange-500/8 p-5 shadow-sm"
              data-testid="hero-cost-callout"
            >
              <div className="flex items-start gap-4">
                <div className="flex flex-col">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-rose-700 dark:text-rose-400">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-rose-500" />
                    </span>
                    Your current monthly cost
                  </span>
                  <div className="mt-1 flex items-baseline gap-1.5">
                    <span className="font-display text-[2.25rem] font-black leading-none tracking-[-0.025em] text-rose-600 dark:text-rose-400 sm:text-[2.75rem]">
                      $<Counter value={TOTAL_REPLACED} duration={1.5} />
                    </span>
                    <span className="font-display text-base font-bold text-rose-600/70 dark:text-rose-400/70 sm:text-lg">
                      /mo
                    </span>
                  </div>
                </div>
              </div>

              {/* Three pain stats — prominently displayed */}
              <div className="grid grid-cols-3 gap-2 border-t border-rose-500/20 pt-3.5">
                {[
                  { n: "12", l: "Logins" },
                  { n: "0",  l: "Integration" },
                  { n: "0",  l: "ROI" },
                ].map((s) => (
                  <div
                    key={s.l}
                    className="text-center"
                    data-testid={`hero-pain-stat-${s.l.toLowerCase()}`}
                  >
                    <p className="font-display text-2xl font-black tracking-[-0.02em] text-rose-600 dark:text-rose-400 sm:text-3xl">
                      {s.n}
                    </p>
                    <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                      {s.l}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.32 }}
              className="mt-6 flex flex-col items-start gap-4"
            >
              <div className="flex flex-wrap items-center gap-3">
                <button
                  data-testid="hero-book-demo"
                  onClick={openDemo}
                  className="group inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-foreground px-7 text-sm font-semibold text-background shadow-lg shadow-foreground/15 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-foreground/25 sm:text-[15px]"
                >
                  <CalendarCheck className="h-4 w-4" />
                  Book a Demo
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </button>
                <a
                  href="#meet-clinic-os"
                  data-testid="hero-try-clinic-os"
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById("meet-clinic-os")
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="group inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-violet-500/40 bg-gradient-to-r from-violet-500/12 via-blue-500/10 to-emerald-500/10 px-5 text-sm font-bold text-foreground/90 backdrop-blur transition-all hover:-translate-y-0.5 hover:border-violet-500/60 hover:bg-violet-500/15 sm:text-[15px]"
                >
                  <Sparkles className="h-4 w-4 text-violet-500" />
                  Try Clinic OS
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </a>
              </div>

              {/* Reassurance row */}
              <div
                className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground"
                data-testid="hero-reassurances"
              >
                {REASSURANCES.map((r, i) => (
                  <span key={r} className="inline-flex items-center gap-1.5">
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
                    {r}
                    {i < REASSURANCES.length - 1 && (
                      <span className="ml-1.5 h-1 w-1 rounded-full bg-border" />
                    )}
                  </span>
                ))}
              </div>

              {/* Partner pills */}
              <div className="flex items-center gap-3" data-testid="hero-partner-strip">
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                  Live with
                </span>
                <Link
                  to="/integrations/juvonno"
                  data-testid="hero-partner-juvonno"
                  className="group inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1.5 transition-all hover:-translate-y-0.5 hover:border-foreground/40 hover:shadow-sm"
                >
                  <img loading="lazy" decoding="async"
                    src="https://www.google.com/s2/favicons?domain=juvonno.com&sz=256"
                    alt="Juvonno logo"
                    className="h-5 w-5 rounded object-contain"
                  />
                  <span className="font-display text-xs font-bold tracking-tight text-foreground/85 group-hover:text-foreground">
                    Juvonno
                  </span>
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  </span>
                </Link>
                <Link
                  to="/integrations/jane-app"
                  data-testid="hero-partner-jane"
                  className="group inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1.5 transition-all hover:-translate-y-0.5 hover:border-foreground/40 hover:shadow-sm"
                >
                  <img loading="lazy" decoding="async"
                    src="https://www.google.com/s2/favicons?domain=jane.app&sz=256"
                    alt="Jane App logo"
                    className="h-5 w-5 rounded object-contain"
                  />
                  <span className="font-display text-xs font-bold tracking-tight text-foreground/85 group-hover:text-foreground">
                    Jane App
                  </span>
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  </span>
                </Link>
              </div>
            </motion.div>
          </div>

          {/* ─────────  RIGHT — 12-tool grid (hidden on mobile to reduce clutter)  ───────── */}
          <div className="hidden lg:col-span-6 lg:block" data-testid="hero-tools-side">
            <p className="text-center text-[10px] font-bold uppercase tracking-[0.22em] text-muted-foreground sm:text-[11px] lg:text-left">
              What your current stack probably looks like
            </p>

            <motion.div
              initial="hidden"
              animate="show"
              variants={{
                hidden: {},
                show: { transition: { staggerChildren: 0.04, delayChildren: 0.15 } },
              }}
              className="mt-4 grid grid-cols-3 gap-2.5 sm:gap-3"
            >
              {REPLACED_TOOLS.map((t, i) => (
                <ToolCard key={t.name} t={t} i={i} />
              ))}
            </motion.div>
          </div>

          {/* ───────── MOBILE-ONLY condensed tool preview — context for the cost callout above ───────── */}
          <div className="hidden" data-testid="hero-tools-mobile" aria-hidden="true">
            {/* Removed on mobile — the dedicated MobileHero handles everything above the fold.
                Hidden marker kept in case external tests look it up. */}
          </div>
        </div>
      </div>
    </section>
    </>
  );
};
